import { useEffect, useRef, useState } from 'react';
import { useUser } from '@common/hooks/useUser';
import useApiOnboarding from '@admission/apis/onboarding/useApiOnboarding';
import { useRedline } from '@libs/redline';
import { unmask } from 'remask';
import { removeMask } from '@libs/utils/masks/cpf';
import { FormInstance } from 'houseform';
import { useUserStore as useACPUserStore } from '@libs/acp/store/user/useUserStore';

import { identityUpdateContactSendCode, identityUpdateContactVerifyCode } from '@libs/acp/apis/updateAccount/updateAccountAPI';

interface useStepVerificationCodeProps {
  setIsModalOpen: (visible: boolean) => void;
  setShowWarning: (visible: boolean) => void;
  currFlow: string;
  newData: string;
  setCurrentStep: (step: 'newInfo' | 'verificationCode') => void;
}

const useStepVerificationCode = ({ setIsModalOpen, setShowWarning, currFlow, newData, setCurrentStep }: useStepVerificationCodeProps) => {
  const { apiOnboardingCustomer } = useApiOnboarding();
  const codeFormRef = useRef<FormInstance>(null);
  const [wrongTokenErrorMessage, setWrongTokenErrorMessage] = useState<string>('');
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [timer, setTimer] = useState<number>(60);

  useEffect(() => {
    let currentSecond = timer;

    const handle40SecondsTimer = setInterval(() => {
      currentSecond--;
      setTimer(currentSecond);
    }, 1000);

    if (currentSecond === 0) {
      clearInterval(handle40SecondsTimer);
    }

    return () => {
      clearInterval(handle40SecondsTimer);
    };
  }, [timer]);

  const updateACPCustomer = useACPUserStore((state) => state.updateACPCustomer);
  const { id, document, email, phone } = useUser();
  const { track: trackRedline } = useRedline();

  const updateUserStore = () => {
    apiOnboardingCustomer.send().then((customer) => {
      updateACPCustomer({
        email: customer?.email,
        phone: unmask(customer?.celular),
      });
    });
  };

  const handleSubmit = (code: string) => {
    const isPhoneFlow = currFlow === 'celular';
    const contactType = isPhoneFlow ? 'phone' : 'email';
    const key = isPhoneFlow ? 'phone' : 'email';
    const oldValue = isPhoneFlow ? phone : email;

		identityUpdateContactVerifyCode({
        type: contactType,
        token: code,
      }).then(() => {
        trackRedline.userAuthentication.userAttributeUpdated({
          devedorId: id,
          customer_id: document,
          customer_email: email,
          key,
          newValue: newData,
          oldValue,
        });
        setIsLoading(false);
        updateUserStore();
        setShowWarning(true);
        setIsModalOpen(false);
        setCurrentStep('newInfo');
      })
      .catch(() => {
        setWrongTokenErrorMessage('Código incorreto. Confira os digítos e tente novamente.');
        setIsLoading(false);
        setIsButtonDisabled(true);
      });
  };

  const verifyVericationCode = () => {
    setIsLoading(true);
    const tokenField = codeFormRef?.current?.getFieldValue('token')?.value;
    handleSubmit(tokenField);
  };

  function getPhoneHint(phone: string) {
    const phoneHint = phone.replace(/^(\(\d{2}\))\s\d{5}/, '(**) *****');
    return phoneHint;
  }

  function getEmailHint(email: string) {
    const splittedEmail = email.split('@');
    const username = splittedEmail[0];
    const emailProvider = splittedEmail[1];

    const emailHint = `${username?.charAt(0)}${'*'.repeat(username.length - 1)}@${emailProvider?.charAt(0)}${'*'.repeat(emailProvider?.length - 1)}`;
    return emailHint;
  }

  const dataHint = currFlow === 'celular' ? getPhoneHint(newData) : getEmailHint(newData);

  const handleResentToken = () => {
    const isPhoneFlow = currFlow === 'celular';
    const contactType = isPhoneFlow ? 'phone' : 'email';

    const cleanedContact = isPhoneFlow ? removeMask(newData) : newData;

    identityUpdateContactSendCode({
        type: contactType,
        contact: cleanedContact,
      })
      .then(() => {
        setTimer(60);
      });
  };

  const onCompleteField = () => {
    setIsButtonDisabled(false);
  };

  return {
    timer,
    dataHint,
    verifyVericationCode,
    handleResentToken,
    wrongTokenErrorMessage,
    codeFormRef,
    isButtonDisabled,
    onCompleteField,
    isLoading,
  };
};

export default useStepVerificationCode;
